import { useState } from 'react'
import { Box, Button, Pagination, Table } from '@amzn/awsui-components-react'
import { useCollection } from '@amzn/awsui-collection-hooks'
import { NewProject, NewServiceProject } from './projectUtil'
import CreateProjectTableProjectName from './CreateProjectTableProjectName'
import TableDescriptionInput from '../reusable/TableDescriptionInput'
import CreateProjectTableProgramName from './CreateProjectTableProgramName'
import useStore from '../../Store'
import StatusInfoPopover from '../reusable/StatusInfoPopover'
import TableIsActiveInput from '../reusable/TableIsActiveInput'
import { ProjectType } from '../../Constant'
import CreateProjectTableServiceName from './CreateProjectTableServiceName'

interface BulkCreateProjectProps {
    onNewProjectDelete: (id: string, newProjects: NewProject[]) => void
    onNewProjectChange: (id: string, type: string, value: any) => void
    isProgramPreSelected: boolean
    selectedYear: number
    newProjects: (NewProject | NewServiceProject)[]
}

const BulkCreateProjectTable = (props: BulkCreateProjectProps) => {
    const {
        onNewProjectDelete,
        onNewProjectChange,
        isProgramPreSelected,
        selectedYear,
        newProjects,
    } = props

    const canAdmin = useStore((state) => state.canAdmin)

    const columnDefinitions = [
        {
            id: 'project_id',
            header: 'Project ID',
            cell: (item) => item.projectId,
        },
        {
            id: 'project_name',
            header: 'Project Name',
            cell: (item) =>
                'serviceId' in item ? (
                    <CreateProjectTableServiceName
                        item={item}
                        selectedYear={selectedYear}
                        onNewProjectChange={onNewProjectChange}
                    />
                ) : (
                    <CreateProjectTableProjectName
                        id={item.projectId}
                        projectName={item.projectName}
                        projectNameError={item.projectNameError}
                        onProjectNameChange={onNewProjectChange}
                    />
                ),
        },
        {
            id: 'select_program',
            header: 'Program Name',
            cell: (item) => {
                const isServiceProject = 'serviceId' in item
                return (
                    <CreateProjectTableProgramName
                        id={item.projectId}
                        selectedProgram={item.selectedProgram}
                        selectedProgramError={item.selectedProgramError}
                        onTeamSelectedProgramChange={onNewProjectChange}
                        isProgramPreSelected={isProgramPreSelected}
                        selectedYear={selectedYear}
                        projectType={
                            isServiceProject ? ProjectType.SERVICE_PROJECT : ProjectType.PROJECT
                        }
                    />
                )
            },
        },
        {
            id: 'is_active',
            header: (
                <StatusInfoPopover
                    title='Active'
                    popoverContent='Mark true if it is an active project (across years)'
                />
            ),
            cell: (item) => (
                <TableIsActiveInput
                    id={item.projectId}
                    isActive={item.isActive}
                    onIsActiveChange={onNewProjectChange}
                    readOnly={'serviceId' in item}
                />
            ),
        },
        {
            id: 'description',
            header: 'Description',
            cell: (item) => (
                <TableDescriptionInput
                    id={item.projectId}
                    description={item.description}
                    onDescriptionChange={onNewProjectChange}
                    readOnly={'serviceId' in item}
                />
            ),
        },
        {
            id: 'delete',
            header: '',
            cell: (item) => (
                <Button
                    iconName='remove'
                    variant='icon'
                    onClick={() => {
                        onNewProjectDelete(item.projectId, newProjects)
                    }}
                />
            ),
        },
    ]

    const visibleColumns = ['project_name', 'select_program', 'description', 'add', 'delete']
    if (canAdmin) {
        visibleColumns.splice(2, 0, 'is_active')
    }

    const [preferences] = useState({ pageSize: 20 })
    const { items, paginationProps, collectionProps } = useCollection(newProjects, {
        pagination: { pageSize: preferences.pageSize },
        sorting: {},
        selection: {},
    })

    return (
        <Table
            {...collectionProps}
            columnDisplay={columnDefinitions.map((col) => {
                return { id: col.id, visible: col.id !== 'project_id' }
            })}
            variant='embedded'
            columnDefinitions={columnDefinitions}
            items={items}
            loadingText='Loading projects and programs'
            trackBy='projectId'
            pagination={<Pagination {...paginationProps} />}
            empty={
                <Box margin={{ vertical: 'xs' }} textAlign='center' color='inherit'>
                    <b>No Projects to create</b>
                </Box>
            }
        />
    )
}

export default BulkCreateProjectTable
